import { url } from "./MongoConstants";

/**
 * Get forum topics with optional filtering and pagination
 * @param {Object} options - Query options
 * @param {string} options.category - Filter by category (optional)
 * @param {string} options.search - Search term for title/content (optional)
 * @param {number} options.limit - Number of topics to return (default 50)
 * @param {number} options.skip - Number of topics to skip (default 0)
 * @param {string} options.sort - Sort order: newest, oldest, popular, most_replies (default newest)
 * @returns {Promise<Object>} API response with topics data
 */
const getForumTopics = async (options = {}) => {
    try {
        const server = url;
        
        // Build query parameters
        const queryParams = new URLSearchParams();
        
        if (options.category && options.category !== 'all') {
            queryParams.append('category', options.category);
        }
        
        if (options.search) {
            queryParams.append('search', options.search);
        }
        
        if (options.limit) {
            queryParams.append('limit', options.limit.toString());
        }
        
        if (options.skip) {
            queryParams.append('skip', options.skip.toString());
        }
        
        if (options.sort) {
            queryParams.append('sort', options.sort);
        }
        
        const queryString = queryParams.toString();
        const endpoint = queryString ? `/api/forum/topics?${queryString}` : '/api/forum/topics';
        
        const response = await fetch(server + endpoint, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            console.log('Failed to get forum topics');
            return {
                success: false,
                error: 'Failed to get forum topics',
                data: { topics: [], totalCount: 0, hasMore: false }
            };
        }
    } catch (error) {
        console.error('An error occurred while fetching forum topics:', error);
        return {
            success: false,
            error: 'Network error occurred',
            message: error.message,
            data: { topics: [], totalCount: 0, hasMore: false }
        };
    }
};

/**
 * Get a single forum topic by ID
 * @param {string} topicId - The topic ID
 * @returns {Promise<Object>} API response with topic data
 */
const getForumTopic = async (topicId) => {
    try {
        const server = url;
        const response = await fetch(server + `/api/forum/topics/${topicId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            console.log('Failed to get forum topic');
            return {
                success: false,
                error: 'Failed to get forum topic'
            };
        }
    } catch (error) {
        console.error('An error occurred while fetching forum topic:', error);
        return {
            success: false,
            error: 'Network error occurred',
            message: error.message
        };
    }
};

/**
 * Get forum categories with topic counts
 * @returns {Promise<Object>} API response with categories data
 */
const getForumCategories = async () => {
    try {
        const server = url;
        const response = await fetch(server + '/api/forum/categories', {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
            },
        });

        if (response.ok) {
            const data = await response.json();
            return data;
        } else {
            console.log('Failed to get forum categories');
            return {
                success: false,
                error: 'Failed to get forum categories',
                data: []
            };
        }
    } catch (error) {
        console.error('An error occurred while fetching forum categories:', error);
        return {
            success: false,
            error: 'Network error occurred',
            message: error.message,
            data: []
        };
    }
};

// Export the functions
export { getForumTopics, getForumTopic, getForumCategories };
export default getForumTopics;
